// invoiceStatus.tsx — der Rechnungsstatus als gemeinsame Quelle.
//
// Der Status ist ein ABGELEITETER Wert (aus den Positionssummen, siehe
// `deriveStatus` im Backend), kein frei setzbarer Workflow-Zustand. Er stand
// vorher doppelt im Code — einmal als Text in der Detailseite, einmal als
// Ableitung im Service. Hier liegt die Anzeigeseite an einer Stelle.
//
// Die Darstellung folgt bewusst dem Access-Original: ein farbiges Kästchen mit
// der ZAHL. Die Zahl ist dort die eigentliche Arbeitssprache („die 1er machen
// wir zuerst"), und die Farbe ist der schnelle Griff. Der volle Satztext hängt
// am Tooltip — er ist zu lang für eine Tabellenspalte, aber genau das, was man
// beim Zögern braucht.

import * as React from "react";

/** Statustexte wie im Access-Original. */
export const STATUS_TEXT: Record<number, string> = {
  0: "Entwurf — noch nicht gebucht",
  1: "Zahlung Kunde erwartet, Zahlung an Versicherer erforderlich",
  2: "Zahlung Kunde teilweise erfolgt",
  3: "Zahlung Kunde erfolgt, Zahlung an Versicherer erforderlich",
  4: "Zahlung Kunde erfolgt, Zahlung an Versicherer erfolgt",
  5: "Zahlung Kunde erfolgt, Versicherer teilweise erfolgt",
};

/** Kurzform für enge Stellen (Filterleiste, Mobilkarte). */
export const STATUS_KURZ: Record<number, string> = {
  0: "Entwurf",
  1: "Kunde offen, VR offen",
  2: "Kunde teilweise",
  3: "Kunde bezahlt, VR offen",
  4: "erledigt",
  5: "VR teilweise",
};

export const STATUS_CODES = [0, 1, 2, 3, 4, 5] as const;

/**
 * Farbrolle je Status, abgeleitet aus dem Access-Farbschlüssel (0 neutral,
 * 1 orange, 2 gelb, 3 blau, 4 grün, 5 hellgrün) — aber in der gedämpften
 * kvp-rd-Palette statt in Access' Vollton.
 *
 * Farbe ist NIE das einzige Merkmal: die Zahl steht im Kästchen, der Satztext
 * im Tooltip (WCAG 1.4.1).
 */
export const STATUS_TON: Record<number, string> = {
  0: "st-entwurf",
  1: "st-offen",
  2: "st-teilweise",
  3: "st-vr-offen",
  4: "st-erledigt",
  5: "st-vr-teilweise",
};

export function statusText(status: number): string {
  return STATUS_TEXT[status] ?? `Status ${status}`;
}

/**
 * Das farbige Statuskästchen mit der Zahl.
 *
 * `manuellErledigt` bekommt einen eigenen Vermerk im Tooltip: die Rechnung
 * zeigt dann eine 4, obwohl die Positionen noch offene Beträge tragen — ohne
 * den Hinweis wäre das ein Widerspruch, den niemand auflösen kann.
 */
export const StatusBadge: React.FC<{
  status: number;
  manuellErledigt?: boolean;
  /** Zusätzlich zur Zahl den Kurztext zeigen (Detailkopf, Mobilkarte). */
  mitText?: boolean;
}> = ({ status, manuellErledigt, mitText }) => {
  const titel = manuellErledigt
    ? `${statusText(status)} — manuell erledigt`
    : statusText(status);

  return (
    <span className="inv-status" title={titel}>
      <span className={`inv-status-kasten ${STATUS_TON[status] ?? "st-entwurf"}`} aria-hidden>
        {status}
      </span>
      {mitText && <span className="inv-status-text">{STATUS_KURZ[status] ?? titel}</span>}
      <span className="visually-hidden">{titel}</span>
    </span>
  );
};
